<script lang="typescript">
  // packages
  import { formatPercent } from '../utils/format.js';
  import { intcomma, pluralize } from 'journalize';
  import Photos from './Photos.svelte';

  // types
  import type { Readable } from 'svelte/store';
  import type { NationalInterface, PresidentRaceFeed } from '../interfaces';
  export let feed: Readable<NationalInterface>;
    /** @type {import('../types.d').Crosswalk} */
  export let crosswalk;

  export let candidate1;

  export let candidate2;

  export let candidateLookup;

  // find the candidates
  $: candidate_1 = $candidateLookup.get(candidate1);
  $: candidate_2 = $candidateLookup.get(candidate2);

  // candidate headshots
  $: candidate_1_img = crosswalk[candidate1];
  $: candidate_2_img = crosswalk[candidate2];

  // calculating votes
  $: totalVotes = $feed.total_votes ? $feed.total_votes : 0;
  // strings for top number
  $: candidate1PctString = formatPercent(candidate_1.votes, totalVotes);
  $: candidate2PctString = formatPercent(candidate_2.votes, totalVotes);
  // pct as a number for element width
  $: candidate1Width = (candidate_1.votes > 0 && totalVotes > 0)?(candidate_1.votes/totalVotes)*100 : 0;
  $: candidate2Width = (candidate_2.votes > 0 && totalVotes > 0)?(candidate_2.votes/totalVotes)*100 : 0;
  $: remainingWidth = (candidate1Width >= 0 && candidate2Width >= 0)? 100 - (candidate1Width + candidate2Width): 100;
  
  export function findParty(c) {
    let party = c.party;
    if (party == 'dem') {
      return 'color-dem-dark'; 
    } else if (party == 'gop') {
      return 'color-gop-dark';
    }
  }
  
  // notes - candidate 1 is schiff, candidate 2 is garvey. This is not sorted.
  function determineLeadingText(c1,c2, totalVotes){
    let c1_pct = (c1.votes/totalVotes)*100;
    let c2_pct = (c2.votes/totalVotes)*100;
    if (c1.is_winner) { // Schiff
      return `The race has been called for ${c1.family_name} with ${c1_pct.toFixed(1)}% of the votes so far. ${c2.family_name} has ${c2_pct.toFixed(1)}% of votes.`
    } else if (c2.is_winner) { // Garvey
      return `The race has been called for ${c2.family_name} with ${c2_pct.toFixed(1)}% of the votes so far. ${c1.family_name} had ${c1_pct.toFixed(1)}% of votes.`
    } else if ((c1.votes == 0) && (c1.votes == 0)){ // no votes
      return `There are no results yet.`
    } else if (c1_pct > c2_pct) { // if Schiff leads
      return `${c1.family_name} leads with ${c1_pct.toFixed(1)}% of the vote. ${c2.family_name} has ${c2_pct.toFixed(1)}%.`
    } else if (c1_pct < c2_pct) { // if Garvey leads
      return `${c2.family_name} leads with ${c2_pct.toFixed(1)}% of the vote. ${c1.family_name} has ${c1_pct.toFixed(1)}%.`
    } else if ((c1_pct === c2_pct) && (c1_pct > 0) && (c2_pct > 0)){
      return `${c2.family_name} and ${c1.family_name} are tied with ${c2_pct.toFixed(1)}% of votes.`
    } else {
      return ''
    }
  }

  $: ariaLabel = `Bar chart shows the results from the U.S. Senate race between Democrat Adam Schiff and Republican Steve Garvey. Either candidate needs at least 50% to win. ${determineLeadingText(candidate_1, candidate_2, totalVotes)}`;

</script>
  
  <div role="presentation" class="display-flex w-full position-relative text-sans-serif space-y-n2" aria-label="{ariaLabel}">
    <!-- candidate 1 section -->
   <div class="display-flex color-{candidate_1.party}-dark image-style">
      <div class="duo-head left candidate-{candidate_1.party}-headshot">
        <Photos
          name='{candidate_1.given_name} {candidate_1.family_name}'
          src={candidate_1_img.jpg || candidate_1_img.png}
          avif={candidate_1_img.avif}
          webp={candidate_1_img.webp}
          party={candidate_1.party}
        />        
      </div>
   </div>
    
    <div style="flex:1;">
      <!-- electors info -->
       <dl class="display-flex justify-between leading-none text-medium mb-14">    
         <!-- candidate 1 info -->
          <div class="text-left {findParty(candidate_1)}" style="width: 40%;">
            <div class="text-5xl mb-11">{candidate1PctString}%</div>
            <dt class="balance-name text-2xl">
              {candidate_1.family_name}{#if candidate_1.is_winner} <span class="candidate-checkmark" aria-label="checkmark, winner">✓</span>{/if}
            </dt>
          </div>

          <!-- pct win text-->
          <div class="text-center to-win-text text-bold">
            50%
          </div>

          <!-- candidate 2 info-->
          <div class="text-right {findParty(candidate_2)}" style="width: 40%;">
            <div class="text-5xl mb-11">{candidate2PctString}%</div>
            <dt class="balance-name text-2xl">
              {#if candidate_2.is_winner}<span class="candidate-checkmark" aria-label="checkmark, winner">✓</span>{/if} {candidate_2.family_name}
            </dt>
          </div>
        </dl>
    
    <!-- bar chart info -->
    <div class="position-relative">
      <div class="display-flex w-full bar-height">
        <div class="bg-{candidate_1.party} l-bar" style="width: {candidate1Width}%" />
        {#if remainingWidth > 0}
          <div class="bg-neutral g-bar" style="width: {remainingWidth}%" />
        {/if}
        <div class="bg-{candidate_2.party} r-bar" style="width: {candidate2Width}%" />
      </div>

      <!-- balance bar -->
      <div
        class="display-flex justify-center w-full position-absolute top-0 bar-height">
        <div class="balance-bar" />
      </div>

      <!-- votes display -->
      <div class="display-flex justify-between text-sm color-grey mt-8">
        <dt class="balance-name">
          <p class="vote-totals color-grey m-0">
            {intcomma(candidate_1.votes)}
            vote{pluralize(candidate_1.votes)}
          </p>
        </dt>
        <dt class="balance-name">
          <p class="vote-totals color-grey m-0">
            {intcomma(candidate_2.votes)}
            vote{pluralize(candidate_2.votes)}
          </p>
        </dt>
      </div>
    </div>
  </div>

  <!-- candidate 2 section -->
    <div class="display-flex justify-between text-sm color-dark-grey image-style">
      <div class="text-right bop-side {findParty(candidate_2)}">
        <div class="duo-head right candidate-{candidate_2.party}-headshot">
          <Photos
            name="{candidate_2.given_name} {candidate_2.family_name}"
            src={candidate_2_img.jpg || candidate_2_img.png}
            avif={candidate_2_img.avif}
            webp={candidate_2_img.webp}
            party={candidate_2.party}
          />
        </div>
      </div>
    </div>
  </div>
  